import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Baby,
  Heart,
  Stethoscope,
  Activity,
  Wind,
  Thermometer,
  Zap,
  Droplet,
  AlertTriangle,
  Brain,
  Shield,
  Pill,
  Scale,
  Sparkles,
  Loader2,
  CheckCircle2
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsPediatrieNeontalogiePage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (pathologyId, pathologyName, content) => {
    setAiLoading({ ...aiLoading, [pathologyId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_pediatrie',
          audience: 'pediatric',
          prompt: `Analyse pédiatrique pour ${pathologyName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Points clés du diagnostic pédiatrique
2. Éléments de gravité à rechercher
3. Particularités de la prise en charge en pédiatrie
4. Dosages adaptés au poids
5. Critères d'hospitalisation en pédiatrie`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [pathologyId]: data.response });
        toast.success(`Analyse AI ${pathologyName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [pathologyId]: false });
    }
  };

  const protocols = [
    {
      id: 'constantes-posologies',
      name: 'Constantes et Posologies Pédiatriques',
      icon: Scale,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**CONSTANTES ET POSOLOGIES PÉDIATRIQUES**

**ESTIMATION DU POIDS:**
• Poids réel préférable
• Poids estimé: utiliser bande de Broselow, PAWPER XL MAC, ou MERCY
• ⚠️ Formules basées sur l'âge non fiables

**CONSTANTES VITALES:**
• **FC (Fréquence Cardiaque):**
  - < 1 an: 80-175 bpm
  - > 1 an: 60-100 bpm

**REMPLISSAGE:**
• 20 ml/kg de NaCl 0,9%

**CHOC ÉLECTRIQUE:**
• **ACR:** 4 J/kg (mode asynchrone)
• **Hors ACR:** 1 J/kg (mode synchrone)

**MÉDICAMENTS ESSENTIELS:**

**ADRÉNALINE:**
• **Dose:** 10 µg/kg
• **Préparation:** 0,1 ml/kg de solution à 1mg/10ml

**AMIODARONE:**
• **Dose:** 5 mg/kg
• **Préparation:** diluer dans G5% pour volume final 30ml

**ATROPINE:**
• **Dose:** 20 µg/kg
• **Préparation:** diluer à 100 µg/mL

**SUFENTANIL:**
• **Dose initiale:** 0,2 µg/kg
• **Entretien:** 0,2 µg/kg/h
• **Préparation:** diluer à 1 µg/mL

**MIDAZOLAM:**
• **Dose initiale:** 0,05 mg/kg
• **Entretien:** 0,05-0,1 mg/kg/h
• **Préparation:** diluer à 1 mg/mL (ou 100 µg/mL pour nouveau-nés)

**PERFUSION DE MAINTIEN:**
• Polyionique G5 ou NaCl
• **Débit basé sur le poids:**
  - Adapter selon l'âge et le poids de l'enfant`
    },
    {
      id: 'douleur-enfant',
      name: 'Douleur chez l\'Enfant',
      icon: AlertTriangle,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**DOULEUR CHEZ L'ENFANT**

**ÉVALUATION:**
• **EVENDOL:** pour enfants 0-7 ans
• **EVA (Échelle Visuelle Analogique):** pour enfants > 6 ans

**CRITÈRES DE GRAVITÉ:**
• Score EVA/EVENDOL élevé
• Signes cliniques d'altération

**TRAITEMENT SELON LA DOULEUR:**

**DOULEUR LÉGÈRE À MODÉRÉE:**
• **Paracétamol:**
  - Dose: selon le poids
  - Voie orale ou intraveineuse

**DOULEUR MODÉRÉE À SÉVÈRE:**
• **Nalbuphine:**
  - Indication: douleur modérée à sévère
  - Posologie adaptée au poids

• **Kétamine:**
  - Pour analgésie procédurale
  - Dosage spécifique

**DOULEUR SÉVÈRE:**
• **Morphine:**
  - Titration selon le poids
  - Surveillance respiratoire étroite
  - Antidote disponible (Naloxone)

**SURVEILLANCE:**
• Fréquence respiratoire
• Saturation en oxygène
• Niveau de sédation
• Efficacité analgésique

**PRÉCAUTIONS:**
• Toujours adapter les doses au poids de l'enfant
• Surveiller les effets secondaires (dépression respiratoire)
• Réévaluer régulièrement la douleur`
    },
    {
      id: 'polytrauma-pediatrique',
      name: 'Polytraumatisé Pédiatrique',
      icon: Activity,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**PARTICULARITÉS PÉDIATRIQUES DU POLYTRAUMATISÉ**

**CLASSIFICATION PAR GRAVITÉ:**

**GRADE A - Critique:**
• Détresse respiratoire sévère
• État de choc
• Glasgow < 9
• Trauma pénétrant
• Trauma thoracique ouvert
• Trauma rachidien avec déficit
• Trauma pelvien instable
• Amputation traumatique
• eFAST positif

**GRADE B - Urgent:**
• Signes de détresse modérée
• Trauma crânien isolé avec Glasgow 9-13
• Fractures multiples
• Brûlures étendues

**GRADE C - Stable:**
• Pas de détresse vitale
• Trauma isolé

**RÉGULATION:**
• Bilan initial rapide
• Régulation tripartite avec réanimateur pédiatrique
• Orientation précoce vers centre spécialisé

**PRISE EN CHARGE INITIALE:**

**AIRWAY (Voies aériennes):**
• Protection rachis cervical
• Oxygénation
• Intubation si nécessaire (particularités anatomiques)

**BREATHING (Respiration):**
• Oxygénation haute concentration
• Ventilation adaptée au poids

**CIRCULATION:**
• 2 VVP gros calibre
• Remplissage: 20 ml/kg NaCl 0,9%
• Éviter l'hypotension (PAS < 70 + (2 x âge) mmHg)

**TRANSFUSION:**
• **CGR (Concentrés Globules Rouges):** 15-20 ml/kg
• **PFC (Plasma Frais Congelé):** 15-20 ml/kg
• **Fibrinogène:** 50-70 mg/kg si < 1,5 g/L
• **Calcium chloride:** 0,2 ml/kg (10%)
• **Acide tranexamique:** 15 mg/kg en 10 min, puis 2 mg/kg/h
• **Plaquettes:** si < 50 000/mm³

**PARTICULARITÉS:**
• Hypothermie plus rapide chez l'enfant
• Compensation hémodynamique longtemps préservée
• Décompensation brutale possible
• Protection cérébrale prioritaire`
    },
    {
      id: 'laryngite',
      name: 'Laryngite',
      icon: Wind,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-400/30',
      content: `**LARYNGITE**

**DIAGNOSTIC:**
• Stridor inspiratoire
• Dysphonie
• Voix étouffée/rauque
• Toux aboyante

**SIGNES DE GRAVITÉ:**
• Stridor au repos
• Tirage important
• Cyanose
• Altération de la conscience
• Épuisement respiratoire

**CONDUITE À TENIR:**

**POSITION ET SUPPORT:**
• Position demi-assise
• Oxygène au masque
• VVP (garde veine)
• Monitoring (scope, SpO2)

**NÉBULISATIONS:**
• **Budésonide:** 2 mg en nébulisation
• **Adrénaline:** 0,1 mg/kg (max 1 mg) avec NaCl 0,9%
• **+/- Ipratropium bromure:** 0,5 mg

**CORTICOTHÉRAPIE:**
• **Bétaméthasone:** 10 gouttes/kg PO
• **OU Méthylprednisolone:** 2 mg/kg IV

**HYDRATATION:**
• Polyionique G5 selon le poids:
  - < 10 kg: 4 ml/kg/h
  - 10-20 kg: 40 ml + 2 ml/kg/h (pour kg au-dessus de 10)
  - > 20 kg: 60 ml + 1 ml/kg/h (pour kg au-dessus de 20)

**OPTIONS AVANCÉES:**
• **HFNC (High-Flow Nasal Cannula)** ou VNI: discuter avec pédiatre
• **Intubation:** si détresse majeure persistante
  - Sonde de calibre inférieur à l'habituel
  - Considérer cricothyroïdotomie si échec
  - Anesthésie: Kétamine, Atropine, Rocuronium

**TRANSPORT:**
• Médicalisé si signes de gravité
• Vers service de pédiatrie avec USC/Réanimation`
    },
    {
      id: 'epiglottite',
      name: 'Épiglottite',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**ÉPIGLOTTITE**

**DIAGNOSTIC:**
• Sepsis avec détresse respiratoire inspiratoire
• Fièvre élevée
• Hypersialorrhée (bave)
• Début rapide
• Voix étouffée
• Dysphagie
• Position en trépied
• Absence de vaccination anti-Hib

**ÂGE:**
• 6 mois à l'âge adulte
• Pic d'incidence: 2-7 ans

**SIGNES DE GRAVITÉ:**
• Risque d'obstruction complète des voies aériennes
• Risque de méningite associée
• Insuffisance respiratoire aiguë

**CONDUITE À TENIR:**

**⚠️ RÈGLE D'OR:**
• **Ne pas manipuler les voies aériennes**
• **Ne pas coucher l'enfant**
• **Calmer l'enfant** (présence parentale)
• **Éviter tout examen pharyngé**

**TRAITEMENT:**
• Position assise confortable
• Oxygène au masque (sans forcer)
• **Nébulisations** (discuter si détresse):
  - Adrénaline: 0,1 mg/kg (max 1 mg)
  - Budésonide: 2 mg

• **VVP** (si possible sans aggraver la détresse)

• **Réhydratation IV:**
  - Polyionique G5 selon poids

• **ANTIBIOTHÉRAPIE IV PRÉCOCE:**
  - **Ceftriaxone:** 50 mg/kg (max 2g) en 1 injection/j
  - **OU Céfotaxime:** 50 mg/kg toutes les 6-8h (max 12g/j)

**INTUBATION:**
• Discuter avec anesthésiste pédiatrique
• Uniquement si détresse majeure
• Conditions optimales au bloc opératoire si possible
• Fibroscope disponible
• Sonde de calibre inférieur

**TRANSPORT:**
• Médicalisé systématique
• Vers réanimation pédiatrique
• Surveillance continue des voies aériennes`
    },
    {
      id: 'bronchiolite',
      name: 'Bronchiolite',
      icon: Wind,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**BRONCHIOLITE**

**DIAGNOSTIC:**
• Infection virale (VRS++)
• Dyspnée avec polypnée
• Wheezing (sibilants)
• Fièvre
• Toux
• Rhinorrhée
• Difficultés alimentaires
• Apnées (surtout < 1 mois)

**ÂGE:**
• Nourrissons < 2 ans
• Épidémie automno-hivernale

**SIGNES DE GRAVITÉ:**
• Âge < 6 semaines
• Prématurité
• Cardiopathie, pathologie respiratoire chronique
• Apnées
• FR > 60/min
• SpO2 < 92% en air ambiant
• Signes de lutte importants
• Refus alimentaire complet
• Déshydratation

**CONDUITE À TENIR:**

**MESURES GÉNÉRALES:**
• Position proclive 30°
• Désobstruction rhinopharyngée (DRP) douce
• Oxygène au masque ou lunettes (objectif SpO2 > 92%)
• VVP (garde veine)
• Monitoring continu

**SONDE NASOGASTRIQUE:**
• Si distension abdominale importante

**HYDRATATION:**
• Polyionique G5 selon poids
• Éviter la surcharge hydrique

**VENTILATION NON INVASIVE:**
• **VNI ou CPAP:** discuter si:
  - Détresse respiratoire persistante
  - Hypoxémie malgré O2
  - Épuisement

**NÉBULISATION:**
• **Salbutamol:** uniquement si très spastique
  - Efficacité variable en bronchiolite
  - Évaluer la réponse

**SI ÉCHEC VNI:**
• Intubation orotrachéale
• Ventilation mécanique:
  - FiO2 adaptée
  - Faibles pressions
  - Temps expiratoire long

**TRANSPORT:**
• Vers pédiatrie ou réanimation selon gravité
• Surveillance continue durant le transport`
    },
    {
      id: 'asthme-aigu-grave',
      name: 'Asthme Aigu Grave Pédiatrique',
      icon: Wind,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**ASTHME AIGU GRAVE PÉDIATRIQUE**

**DIAGNOSTIC:**
• Exacerbation sévère d'asthme (ESA)
• Engagement du pronostic vital

**FACTEURS DE GRAVITÉ:**
• ATCD d'hospitalisation en réanimation
• Corticothérapie orale récente
• Surconsommation de β2-mimétiques
• Âge > 70 ans (adulte)
• Difficulté à parler
• Altération de conscience
• État de choc
• Tachypnée > 30/min
• Hypoxémie (SpO2 < 90%)
• Hypercapnie (PaCO2 > 45 mmHg)
• ATCD d'infection récente
• Pneumothorax
• Sensibilisation particulière

**CONDUITE À TENIR:**

**POSITION:**
• Position assise confortable

**OXYGÉNOTHÉRAPIE:**
• O2 au masque à haute concentration
• Objectif SpO2 > 92%

**BRONCHODILATATEURS:**
• **Salbutamol en continu:**
  - Nébulisation continue 5 mg/h
  - Ou 2,5-5 mg toutes les 20 minutes

• **Ipratropium bromure:**
  - 0,5 mg en nébulisation
  - Associé au Salbutamol

**CORTICOTHÉRAPIE:**
• **IV:** Méthylprednisolone 2 mg/kg
• **OU PO:** Prednisolone 1-2 mg/kg

**SI FORME SÉVÈRE:**
• **Sulfate de Magnésium:**
  - 40 mg/kg IV lent (max 2g)
  - Sur 20-30 minutes
  - Sous monitoring cardiaque

**VENTILATION MÉCANIQUE:**
• Indication en dernier recours:
  - Épuisement respiratoire
  - Hypercapnie majeure
  - Troubles de conscience
  - Arrêt cardio-respiratoire

• **Paramètres:**
  - Faibles pressions
  - Temps expiratoire long
  - Hypercapnie permissive
  - FiO2 minimale nécessaire

**⚠️ ÉVITER:**
• Sédation trop profonde (risque d'hypotension)
• Volumes courants élevés
• Hyperventilation

**TRANSPORT:**
• Médicalisé vers réanimation pédiatrique`
    },
    {
      id: 'hypoglycemie-pediatrique',
      name: 'Hypoglycémie Pédiatrique',
      icon: Droplet,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**HYPOGLYCÉMIE PÉDIATRIQUE**

**DIAGNOSTIC:**
• **Glycémie < 2,8 mmol/L (< 0,50 g/L)**

**SIGNES CLINIQUES:**
• Sueurs, pâleur
• Tremblement
• Tachycardie
• Troubles du comportement
• Convulsions
• Troubles de conscience
• Coma

**PRISE EN CHARGE:**

**SI ENFANT CONSCIENT:**
• **Glucose oral:**
  - Sucres rapides (boisson sucrée, morceaux de sucre)
  - Lait maternel pour nourrisson
  - Pain

• **Surveillance:**
  - Contrôle glycémique après 15 minutes
  - Renouveler si nécessaire

**SI TROUBLES DE CONSCIENCE:**

**< 25 kg:**
• **Glucagon:**
  - 0,5 mg SC ou IM
  - Peut être renouvelé

**> 25 kg:**
• **Glucagon:**
  - 1 mg SC ou IM

**SI ÉCHEC DU GLUCAGON:**
• **Glucose IV:**
  - Bolus: 0,5 g/kg (soit 2,5 ml/kg de G20% ou 5 ml/kg de G10%)
  - Puis perfusion continue:
    > Nouveau-né: 5-8 mg/kg/min
    > Nourrisson: 3-5 mg/kg/min
    > Enfant: 2-3 mg/kg/min

**SURVEILLANCE:**
• **Glycémie capillaire:**
  - Toutes les heures initialement
  - Puis toutes les 4 heures
  - Puis toutes les 24 heures une fois stabilisé

**RECHERCHE ÉTIOLOGIQUE:**
• Diabète traité
• Jeûne prolongé
• Intoxication médicamenteuse
• Trouble métabolique
• Infection sévère
• Insuffisance hépatique

**TRANSPORT:**
• Vers pédiatrie pour surveillance et bilan étiologique
• Perfusion de glucose maintenue durant le transport`
    },
    {
      id: 'acidocetose-diabetique',
      name: 'Acidocétose Diabétique',
      icon: Droplet,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**ACIDOCÉTOSE DIABÉTIQUE EN PÉDIATRIE**

**DIAGNOSTIC:**
• Polyurie, polydipsie
• Vomissements
• Douleurs abdominales
• Fièvre
• Respiration de Kussmaul (ample et rapide)
• Altération de conscience
• Déshydratation

**BIOLOGIE:**
• **Hyperglycémie** (généralement > 11 mmol/L)
• **Acidose:** pH < 7,38, HCO3- < 20 mmol/L
• **Cétonémie** > 0,5 mmol/L ou cétonurie positive

**ÉTIOLOGIES:**
• Diabète inaugural (30-40% des cas)
• Infection
• Non-observance thérapeutique
• Autres maladies intercurrentes
• Causes iatrogènes

**SIGNES DE GRAVITÉ:**
• Altération de conscience (Glasgow < 12)
• État de choc
• Défaillance d'organe
• Hypokaliémie < 3,5 mmol/L

**CONDUITE À TENIR:**

**ACCÈS VASCULAIRE:**
• VVP (2 si possible)
• Monitoring continu

**RÉHYDRATATION INITIALE:**
• **Si choc:** NaCl 0,9% 20 ml/kg en 30 min
  - Renouvelable 1 fois si persistance du choc

• **Calcul du déficit hydrique:**
  - Déshydratation estimée (%) x poids (kg) = volume déficit (L)

• **Réhydratation progressive:**
  - Compenser le déficit sur 48h
  - Utiliser solution équilibrée (NaCl 0,45% + KCl)

**AJOUT DE GLUCOSE:**
• Quand glycémie < 15 mmol/L (2,7 g/L)
• Ajouter G5% ou G10% à la perfusion

**INSULINOTHÉRAPIE:**
• **NE PAS débuter avant 1h de réhydratation**
• **Insuline rapide IVSE:**
  - 0,05-0,1 UI/kg/h
  - Objectif: baisse glycémique progressive (3-5 mmol/L/h)

**SUPPLÉMENTATION EN POTASSIUM:**
• **Surveillance stricte de la kaliémie**
• **Apport:** 2-4 mmol/kg/24h
• **Adaptation selon kaliémie:**
  - < 3,5 mmol/L: augmenter apports
  - > 5,5 mmol/L: arrêter temporairement

**SURVEILLANCE:**
• Glycémie horaire
• Ionogramme (kaliémie ++) toutes les 2-4h
• Gazométrie artérielle
• État neurologique
• Diurèse

**⚠️ COMPLICATIONS:**
• **Œdème cérébral** (rare mais grave):
  - Céphalées, vomissements
  - Troubles de conscience
  - HTA, bradycardie
  - Traitement: Mannitol 0,5-1 g/kg IV ou SSH 3%

**TRANSPORT:**
• Médicalisé vers réanimation pédiatrique
• Poursuite réhydratation et insulinothérapie`
    },
    {
      id: 'convulsions-febriles',
      name: 'Crise Convulsive Hyperthermique',
      icon: Thermometer,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**CRISE CONVULSIVE HYPERTHERMIQUE**

**DIAGNOSTIC:**
• Crise tonico-clonique
• Enfant 6 mois - 5 ans
• Associée à fièvre (> 38°C)
• Aucune autre cause identifiable
• Pas d'infection du SNC
• Pas d'ATCD neurologique

**CRITÈRES DE GRAVITÉ:**
• Durée > 15 minutes
• Caractère focal
• Répétition des crises
• Âge < 6 mois ou > 5 ans
• Déficit post-critique persistant

**CONDUITE À TENIR:**

**SI LA CRISE S'ARRÊTE SPONTANÉMENT:**
• Position latérale de sécurité
• Libération des voies aériennes
• Oxygénation
• Surveillance
• Antipyrétique (Paracétamol)
• Rechercher foyer infectieux
• Rassurer les parents

**SI CRISE PERSISTANTE OU RÉCURRENTE:**

**MESURES GÉNÉRALES:**
• Position latérale de sécurité
• Oxygène au masque
• Aspiration si nécessaire
• VVP ou intra-osseuse
• Monitoring
• Glycémie capillaire

**TRAITEMENT ANTICONVULSIVANT:**

**1ère LIGNE - BENZODIAZÉPINES:**
• **Clonazépam (Rivotril®) IV:**
  - < 30 kg: 0,5 mg
  - > 30 kg: 1 mg
  - Renouveler 1 fois si besoin

• **OU Midazolam IV:**
  - 0,1-0,2 mg/kg (max 10 mg)

• **OU Diazépam rectal:**
  - < 10 kg: 5 mg
  - 10-15 kg: 7,5 mg
  - > 15 kg: 10 mg

**2ème LIGNE (si échec benzodiazépines):**
• Envisager antiépileptiques de 2ème ligne
• Discuter avec pédiatre

**RECHERCHE ÉTIOLOGIQUE:**
• Recherche foyer infectieux (ORL, pulmonaire, urinaire)
• Éliminer une méningite si doute
  - Ponction lombaire si signes méningés
  - Antibiothérapie probabiliste si nécessaire

**TRANSPORT:**
• Vers pédiatrie pour surveillance
• Médicalisé si convulsions prolongées ou récurrentes`
    },
    {
      id: 'etat-mal-convulsif',
      name: 'État de Mal Convulsif Pédiatrique',
      icon: Zap,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**ÉTAT DE MAL CONVULSIF PÉDIATRIQUE**

**DÉFINITION:**
• Crise convulsive généralisée durant > 5 minutes
• OU ≥ 2 crises sans récupération de conscience entre elles

**ÉTAT DE MAL RÉFRACTAIRE:**
• Persistance de l'EME malgré 2 lignes de traitement

**CONDUITE À TENIR:**

**MESURES GÉNÉRALES:**
• Position latérale de sécurité
• Libération et protection des voies aériennes
• Oxygène haute concentration
• Aspiration si nécessaire
• VVP ou IO
• Monitoring continu (scope, SpO2, TA)
• Glycémie capillaire

**PERFUSION:**
• Polyionique G5 selon poids

**1ère LIGNE (0-5 min) - BENZODIAZÉPINES:**
• **Clonazépam (Rivotril®) IV:**
  - < 30 kg: 0,5 mg IVL en 30 sec
  - > 30 kg: 1 mg IVL en 30 sec
  - Renouveler 1 fois après 5 min si persistance

• **OU Midazolam:**
  - 0,15 mg/kg IV (max 10 mg)
  - OU 0,2-0,3 mg/kg IM/IN si pas de voie veineuse

• **OU Diazépam rectal:**
  - < 10 kg: 5 mg
  - 10-15 kg: 7,5 mg
  - > 15 kg: 10 mg

**2ème LIGNE (10-20 min):**

• **Fosphénytoïne (Pro-Dilantin®):**
  - Dose de charge: 20 mg EP/kg IV
  - Perfusion lente (max 3 mg EP/kg/min)
  - Monitoring cardiaque obligatoire

• **OU Phénobarbital:**
  - Dose de charge: 20 mg/kg IV lent
  - Max 1 mg/kg/min
  - Risque de dépression respiratoire

• **OU Lévétiracétam (Keppra®):**
  - 40-60 mg/kg IV (max 3000 mg)
  - Sur 5-10 minutes

**3ème LIGNE (30-40 min) - ÉTAT DE MAL RÉFRACTAIRE:**
• **Intubation et ventilation mécanique**
• **Sédation:**
  - Midazolam: bolus 0,2 mg/kg puis 0,1-0,4 mg/kg/h
  - OU Thiopental: bolus 3-5 mg/kg puis 3-5 mg/kg/h
• **EEG continu** si disponible

**RECHERCHE ÉTIOLOGIQUE:**
• Ionogramme (Na, Ca, Mg)
• Glycémie
• Infection (méningite, encéphalite)
• Traumatisme crânien
• Intoxication
• Trouble métabolique
• Épilepsie connue mal équilibrée

**TRANSPORT:**
• Médicalisé vers réanimation pédiatrique
• Ventilation et sédation maintenues si EME réfractaire`
    },
    {
      id: 'coma-pediatrique',
      name: 'Coma Pédiatrique',
      icon: Brain,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**COMA PÉDIATRIQUE**

**ÉVALUATION:**
• **Score de Glasgow pédiatrique** (adapté à l'âge)
• Tenir compte du développement psychomoteur

**GLASGOW PÉDIATRIQUE:**

**Ouverture des yeux (E):**
• Spontanée: 4
• À la demande: 3
• À la douleur: 2
• Aucune: 1

**Réponse verbale (V) - adaptée à l'âge:**
• Babillage approprié / mots appropriés: 5
• Cris consolables / mots inappropriés: 4
• Cris inconsolables / sons: 3
• Gémissements / gémit à la douleur: 2
• Aucune réponse: 1

**Réponse motrice (M):**
• Spontanée / obéit: 6
• Localise la douleur: 5
• Retrait à la douleur: 4
• Flexion anormale (décortication): 3
• Extension anormale (décérébration): 2
• Aucune réponse: 1

**CONDUITE À TENIR:**

**LIBÉRATION DES VOIES AÉRIENNES:**
• Position adaptée (PLS si respiration spontanée)
• Aspiration si nécessaire
• Canule de Guédel si besoin
• Considérer intubation si Glasgow ≤ 8

**OXYGÉNATION:**
• O2 haute concentration
• Objectif SpO2 > 94%

**ACCÈS VASCULAIRE:**
• VVP ou IO

**GLYCÉMIE CAPILLAIRE:**
• Correction immédiate si hypoglycémie

**MONITORING:**
• Scope, SpO2, TA
• ECG

**TRAITEMENT ÉTIOLOGIQUE:**

**CONVULSIONS:**
• Benzodiazépines (cf protocole EME)

**HYPERTENSION INTRACRÂNIENNE:**
• Position tête surélevée 30°
• Éviter compression veines jugulaires
• SSH 3%: 2-5 ml/kg sur 10-20 min
• OU Mannitol 20%: 0,25-1 g/kg sur 20 min
• Éviter hypercapnie et hypoxie

**HYPOGLYCÉMIE:**
• Glucose IV: 0,5 g/kg (G10% ou G20%)

**INTOXICATION:**
• Antidote spécifique si connu
• Naloxone si suspicion opiacés

**INFECTION:**
• Antibiothérapie si suspicion méningite
• Antipyrétique si fièvre

**TRAUMATISME CRÂNIEN:**
• Immobilisation rachis cervical
• Éviter hypotension et hypoxie
• Transport vers neurochirurgie

**BILAN MINIMAL:**
• Glycémie
• Ionogramme
• Gaz du sang
• Scanner cérébral

**TRANSPORT:**
• Médicalisé vers réanimation pédiatrique
• Maintien des fonctions vitales
• Surveillance neurologique continue`
    },
    {
      id: 'choc-septique-enfant',
      name: 'Choc Septique de l\'Enfant',
      icon: Shield,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**CHOC SEPTIQUE DE L'ENFANT**

**DIAGNOSTIC:**
• Sepsis + signes de choc
• Hypotension (PAS < 70 + (2 x âge) mmHg chez enfant 1-10 ans)
• OU signes de mauvaise perfusion:
  - Marbrures, extrémités froides
  - TRC > 2 secondes
  - Tachycardie inappropriée
  - Oligurie
  - Troubles de conscience

**⚠️ Présentation clinique moins évidente que chez l'adulte**

**CONDUITE À TENIR:**

**A - AIRWAY:**
• Libération et protection des voies aériennes
• Oxygène haute concentration

**B - BREATHING:**
• Oxygénation optimale (objectif SpO2 > 94%)
• Considérer intubation si:
  - Détresse respiratoire sévère
  - Troubles de conscience
  - Choc réfractaire

**C - CIRCULATION:**

**ACCÈS VASCULAIRE:**
• 2 VVP gros calibre
• OU voie intra-osseuse si échec VVP (< 90 sec)

**HÉMOCULTURES:**
• Avant antibiotiques si possible
• Ne pas retarder l'antibiothérapie

**ANTIBIOTHÉRAPIE PRÉCOCE:**
• Dans l'heure suivant le diagnostic
• **Ceftriaxone:** 50 mg/kg IV (max 2g)
• **OU Céfotaxime:** 50 mg/kg IV (max 2g) toutes les 6-8h

**REMPLISSAGE VASCULAIRE:**
• **1er bolus:** 20 ml/kg NaCl 0,9% ou Ringer Lactate
  - Sur 5-10 minutes
• **Réévaluation:**
  - Si amélioration: continuer surveillance
  - Si persistance du choc: 2ème bolus 20 ml/kg
• **Maximum:** 60 ml/kg dans la première heure
• **⚠️ Attention:** ne pas surcharger (risque OAP)

**VASOPRESSEURS:**
(Si choc résistant au remplissage)

• **Noradrénaline (1ère intention):**
  - 0,05-1 µg/kg/min IVSE
  - Titration selon PA et perfusion

• **+/- Dobutamine ou Adrénaline:**
  - Si bas débit cardiaque persistant
  - Dobutamine: 5-20 µg/kg/min
  - Adrénaline: 0,05-1 µg/kg/min

**VENTILATION MÉCANIQUE:**
• Indication:
  - Détresse respiratoire
  - Troubles de conscience
  - Instabilité hémodynamique sévère
• Paramètres: ventilation protectrice

**CORTICOTHÉRAPIE:**
• Discuter Hydrocortisone si choc réfractaire aux catécholamines

**SURVEILLANCE:**
• PA, FC, SpO2, TRC, diurèse
• Lactates artériels
• Réévaluation fréquente

**TRANSPORT:**
• Médicalisé vers réanimation pédiatrique
• Maintien remplissage et catécholamines`
    },
    {
      id: 'meningite-enfant',
      name: 'Méningite Enfant et Nourrisson',
      icon: Brain,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**MÉNINGITE CHEZ L'ENFANT ET LE NOURRISSON**

**DIAGNOSTIC:**

**DIFFICILE CHEZ LE NOUVEAU-NÉ ET NOURRISSON:**
• Signes non spécifiques:
  - Fièvre ou hypothermie
  - Refus de boire
  - Vomissements
  - Troubles du comportement
  - Hypotonie ou hypertonie
  - Geignements
  - Fontanelle bombée (tardif)

**NOURRISSON > 2 MOIS ET ENFANT:**
• Signes de sepsis
• Signes d'irritation méningée:
  - Raideur de nuque
  - Photophobie
  - Céphalées intenses
  - Vomissements en jet
  - Hypoacousie

**⚠️ Triade classique (fièvre, céphalées, raideur nuque) a une faible sensibilité chez l'enfant**

**ÉTIOLOGIE:**

**VIRALE (plus fréquente):**
• Entérovirus, HSV, VZV

**BACTÉRIENNE:**
• **Méningocoque** (Neisseria meningitidis)
• **Pneumocoque** (Streptococcus pneumoniae)
• Haemophilus influenzae (rare si vacciné)
• Nouveau-né: Streptocoque B, E. coli, Listeria

**CONDUITE À TENIR:**

**MESURES GÉNÉRALES:**
• VVP
• Monitoring
• Oxygène si nécessaire

**ANTIBIOTHÉRAPIE:**

**AVANT PONCTION LOMBAIRE SI:**
• Purpura fulminans
• Choc septique
• Instabilité hémodynamique
• Troubles de conscience
• Signes d'HTIC
• Convulsions persistantes

**CHOIX ANTIBIOTIQUE:**
• **Nourrisson > 3 mois et enfant:**
  - Ceftriaxone: 50 mg/kg IV (max 2g)
  - OU Céfotaxime: 50 mg/kg IV toutes les 6-8h

• **Nouveau-né et nourrisson < 3 mois:**
  - Céfotaxime: 50 mg/kg IV
  - + Amoxicilline: 50 mg/kg IV (anti-Listeria)

**DEXAMÉTHASONE:**
• Peut être administrée avec ou juste avant les antibiotiques
• Dose: 0,15 mg/kg toutes les 6h pendant 2-4 jours
• Surtout si suspicion de méningite à pneumocoque
• Discuter avec pédiatre

**TRAITEMENT SYMPTOMATIQUE:**
• Antipyrétique: Paracétamol 15 mg/kg
• Anticonvulsivants si nécessaire
• Réhydratation adaptée

**PONCTION LOMBAIRE:**
• Réalisée à l'hôpital si possible
• Contre-indications:
  - Instabilité hémodynamique
  - HTIC
  - Troubles de la coagulation
  - Infection cutanée au site de ponction

**ISOLEMENT:**
• Gouttelettes si suspicion méningocoque
• Jusqu'à 24h d'antibiothérapie

**DÉCLARATION OBLIGATOIRE:**
• Méningite à méningocoque

**TRANSPORT:**
• Médicalisé vers pédiatrie/réanimation
• Antibiothérapie débutée durant le transport`
    },
    {
      id: 'purpura-fulminans',
      name: 'Purpura Fulminans Pédiatrique',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**PURPURA FULMINANS PÉDIATRIQUE**

**DIAGNOSTIC:**
• Purpura fébrile avec lésions nécrotiques/ecchymotiques (> 3mm)
• Souvent associé à un sepsis ou méningococcémie
• Extension rapide des lésions

**⚠️ URGENCE VITALE ABSOLUE**

**SIGNES DE GRAVITÉ:**
• Âge < 1 an
• Délai diagnostic/traitement prolongé
• Extension rapide du purpura
• Ischémie sévère des extrémités
• Défaillance cardio-respiratoire
• Convulsions
• Coma

**CONDUITE À TENIR:**

**MESURES IMMÉDIATES:**
• Position adaptée
• Oxygène haute concentration
• VVP ou IO (ne pas retarder pour cela)
• Monitoring continu
• **Noter l'heure de début des lésions**

**HÉMOCULTURES:**
• Si voie veineuse déjà en place
• **Ne pas retarder l'antibiothérapie**

**ANTIBIOTHÉRAPIE IMMÉDIATE:**
(Dans les 20 premières minutes)

• **Ceftriaxone:** 50 mg/kg IV (max 2g)
• **OU Céfotaxime:** 50 mg/kg IV (max 2g)

**Si pas de voie veineuse:**
• **Ceftriaxone:** 50 mg/kg IM

**REMPLISSAGE VASCULAIRE:**
• NaCl 0,9% ou Ringer Lactate
• Bolus 20 ml/kg sur 5-10 min
• Renouveler selon réponse
• Maximum 60 ml/kg dans la 1ère heure

**VASOPRESSEURS:**
(Si choc résistant au remplissage)

• **Noradrénaline:**
  - 0,05-1 µg/kg/min IVSE
  - Titration selon PA

• **+/- Adrénaline:**
  - Si inefficacité
  - 0,05-1 µg/kg/min

**CORTICOTHÉRAPIE:**
• **Hydrocortisone:** 50 mg/kg en bolus
• Si choc septique réfractaire

**TRAITEMENT SYMPTOMATIQUE:**
• Anticonvulsivants si convulsions
• Ventilation mécanique si détresse respiratoire
• Correction troubles métaboliques

**SURVEILLANCE:**
• PA, FC, SpO2, TRC, diurèse
• État neurologique
• Évolution du purpura (photos si possible)
• Lactates

**⚠️ DÉCLARATION OBLIGATOIRE:**
• Méningite/septicémie à méningocoque

**ANTIBIOPROPHYLAXIE DE L'ENTOURAGE:**
• Rifampicine ou Ciprofloxacine
• Contacts proches dans les 10 jours

**TRANSPORT:**
• Médicalisé URGENT vers réanimation pédiatrique
• Antibiothérapie et remplissage maintenus
• Vasopresseurs si nécessaire`
    },
    {
      id: 'syndrome-bebe-secoue',
      name: 'Syndrome du Bébé Secoué',
      icon: Baby,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**SYNDROME DU BÉBÉ SECOUÉ (SBS)**

**DÉFINITION:**
• Traumatisme crânien non accidentel par secouement
• Souvent sans histoire d'accident clair
• Forte suspicion chez nourrisson < 1 an, surtout < 6 mois

**DIAGNOSTIC:**

**SIGNES CLINIQUES:**
• Troubles neurologiques:
  - Troubles de conscience
  - Convulsions
  - Hypotonie
  - Irritabilité
  - Apnées
  - Coma

• Signes associés:
  - Vomissements
  - Troubles de la succion/alimentation
  - Pâleur
  - Fontanelle bombée

**TRIADE CLINIQUE CLASSIQUE:**
1. **Hémorragies rétiniennes** (très évocatrices)
2. **Hématome sous-dural**
3. **Encéphalopathie**

**⚠️ SIGNES D'ALERTE:**
• Explications parentales:
  - Absentes
  - Imprécises
  - Changeantes
  - Incompatibles avec les lésions
• Retard à la consultation
• Antécédents d'hospitalisation répétées

**LÉSIONS ASSOCIÉES:**
• Fractures des côtes (multiples, âges différents)
• Fractures métaphysaires (bras, jambes)
• Ecchymoses multiples d'âges différents
• Brûlures

**CONDUITE À TENIR:**

**PRISE EN CHARGE MÉDICALE:**
• Libération voies aériennes
• Oxygénation
• Position adaptée (tête surélevée 30° si HTIC)
• VVP
• Monitoring continu
• Glycémie capillaire

**TRAITEMENT SYMPTOMATIQUE:**
• Anticonvulsivants si convulsions
• Traitement HTIC si nécessaire:
  - SSH 3%: 2-5 ml/kg
  - OU Mannitol 20%: 0,25-1 g/kg

**EXAMEN CLINIQUE COMPLET:**
• Recherche lésions associées
• Photos des lésions cutanées
• Mensuration du PC
• Examen neurologique

**⚠️ ASPECTS MÉDICO-LÉGAUX:**

**SIGNALEMENT OBLIGATOIRE:**
• Information au Procureur de la République
• Rédaction certificat médical descriptif détaillé
• Photos des lésions
• Conservation des preuves

**PROTECTION DE L'ENFANT:**
• Hospitalisation systématique
• Séparation temporaire d'avec le milieu familial
• Contact cellule protection de l'enfance

**NE PAS:**
• Confronter agressivement les parents
• Porter des accusations directes
• Minimiser la situation

**EXAMENS PARACLINIQUES:**
• Scanner cérébral en urgence
• Fond d'œil (recherche hémorragies rétiniennes)
• Squelette entier (fractures multiples)
• Bilan biologique (coagulation)

**PRONOSTIC:**
• Décès dans 20-25% des cas
• Séquelles neurologiques graves fréquentes:
  - Épilepsie
  - Retard psychomoteur
  - Cécité
  - Infirmité motrice cérébrale

**TRANSPORT:**
• Médicalisé vers réanimation pédiatrique
• Centre avec neurochirurgie pédiatrique
• Signalement effectué avant transport`
    },
    {
      id: 'min',
      name: 'Mort Inattendue du Nourrisson',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**MORT INATTENDUE DU NOURRISSON (MIN)**

**DÉFINITION:**
• Décès brutal et inattendu d'un nourrisson < 2 ans
• Aucun antécédent ne laissant présager le décès
• Mort Subite du Nourrisson (MSN) = MIN sans cause après enquête complète

**ÂGE:**
• Pic d'incidence: 2-4 mois
• 90% des cas < 1 an

**PROCÉDURE SMUR:**

**RÉANIMATION:**
• Protocole de réanimation standard du nouveau-né/nourrisson
• MCE, ventilation
• Adrénaline, remplissage selon protocole
• Durée minimum: 30 minutes
• Considérer hypothermie possible (prolonger réanimation)

**RECUEIL DE DONNÉES:**
• **Environnement:**
  - Position de couchage (décubitus dorsal, ventral, latéral)
  - Type de lit/couchage
  - Partage du lit (co-sleeping)
  - Température de la pièce
  - Couvertures, oreillers, tour de lit
  - Tabagisme passif

• **Antécédents:**
  - Grossesse, accouchement
  - Poids de naissance
  - Allaitement
  - Vaccinations
  - Maladies récentes
  - Traitement en cours

• **Circonstances:**
  - Heure de découverte
  - Témoin présent
  - Dernière fois vu vivant
  - Tentatives de réanimation

**EXAMEN DU NOURRISSON:**
• Température
• Lividités
• Rigidité
• Recherche lésions traumatiques
• Écoulements (nez, bouche)

**PRÉLÈVEMENTS:**
• Si contexte infectieux: hémocultures
• Conserver échantillons si possible

**ENTRETIEN AVEC LA FAMILLE:**
• Écoute, soutien
• Explication procédure
• Proposition contact psychologue
• Information sur autopsie

**TRANSPORT:**
• Vers hôpital de référence MIN
• Centre avec médecin légiste et plateau technique complet
• **⚠️ Ne pas laisser les parents seuls avec le corps durant le transport**

**OBSTACLE MÉDICO-LÉGAL:**
• Déclarer systématiquement
• Information au Procureur
• Certificat de décès: "Mort inattendue du nourrisson"

**AUTOPSIE:**
• Systématique
• Obligatoire dans le cadre MIN
• Expliquée aux parents (recherche cause, pas accusation)

**BILAN HOSPITALIER:**
• Radiographie du squelette entier
• Scanner cérébral
• Prélèvements biologiques
• Fond d'œil si suspicion maltraitance

**SUIVI DES PARENTS:**
• Soutien psychologique
• Groupe de parole
• Suivi par PMI
• Information sur facteurs de risque pour fratrie

**PRÉVENTION (Messages aux parents):**
• Couchage sur le dos
• Lit adapté (matelas ferme)
• Pas de co-sleeping
• Pas d'oreiller, couverture épaisse
• Température chambre 18-20°C
• Pas de tabagisme passif
• Allaitement maternel protecteur
• Vaccinations à jour`
    },
    {
      id: 'baveu',
      name: 'BAVEU - Bonne Adaptation Vie Extra-Utérine',
      icon: Baby,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      border: 'border-green-400/30',
      content: `**BAVEU - BONNE ADAPTATION À LA VIE EXTRA-UTÉRINE**

**DÉFINITION:**
• Nouveau-né à terme qui:
  - Pleure ou crie
  - Respire spontanément
  - Bon tonus musculaire

**⚠️ Absence d'un de ces items = MAVEU (réanimation nécessaire)**

**PRISE EN CHARGE MINIMALE HORS HÔPITAL:**

**HYGIÈNE:**
• Gants, masque
• Champs propres
• Désinfection matériel

**THERMORÉGULATION:**
• **Priorité absolue**
• Sécher le nouveau-né immédiatement
• Enlever linge humide
• Peau-à-peau avec la mère
• Bonnet
• Couverture sèche
• Éviter les courants d'air
• Chauffage du véhicule

**ASPIRATION:**
• **Uniquement si sécrétions abondantes**
• Aspiration douce bouche et nez
• Ne pas stimuler le fond de la gorge (réflexe vagal)

**CLAMPAGE DU CORDON:**
• **Délai:** 1 à 3 minutes après naissance si BAVEU
• Cordon pince par 2 pinces
• Section entre les pinces
• Nœud ou pince à demeure sur cordon côté bébé

**SCORE D'APGAR:**
• À 1, 5 et 10 minutes de vie
• 5 critères (FC, respiration, tonus, réactivité, coloration)
• Chaque critère coté 0, 1 ou 2

**SURVEILLANCE:**

**SATURATION ET FC:**
• Objectifs par âge:
  - 2 min: SpO2 ≥ 60%, FC ≥ 100 bpm
  - 3 min: SpO2 ≥ 70%, FC ≥ 100 bpm
  - 5 min: SpO2 ≥ 80%, FC ≥ 100 bpm
  - 10 min: SpO2 ≥ 90%, FC ≥ 100 bpm

**GLYCÉMIE:**
• Surveillance si facteurs de risque:
  - Mère diabétique
  - Prématurité
  - Petit poids de naissance
  - Détresse
• Objectif: glycémie > 2,5 mmol/L (0,45 g/L)

**EXAMEN CLINIQUE:**
• Morphologie générale
• Perméabilité des choanes
• Perméabilité anale
• Examen du dos (spina bifida)
• Palpation abdomen
• Recherche malformations

**ALLAITEMENT:**
• Favoriser mise au sein précoce (< 2h)
• Peau-à-peau prolongé

**PRÉVENTION HYPOTHERMIE:**
• Maintenir température > 36,5°C
• Vérifier température régulièrement

**PAS D'INDICATION À:**
• Vitamine K en pré-hospitalier
• Collyre antibiotique
• Bain

**TRANSPORT:**
• Vers maternité ou SAU avec pédiatrie
• Maintien température
• Surveillance continue
• Mère et enfant ensemble si possible

**INFORMATION DES PARENTS:**
• Surveillance normale
• Signes d'alerte (difficultés respiratoires, refus de téter, changement de couleur)
• Suivi pédiatrique à prévoir`
    },
    {
      id: 'maveu',
      name: 'MAVEU - Mauvaise Adaptation Vie Extra-Utérine',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**MAVEU - MAUVAISE ADAPTATION À LA VIE EXTRA-UTÉRINE**

**DÉFINITION:**
• Nouveau-né prématuré
• OU nouveau-né qui ne pleure pas/ne respire pas
• OU hypotonie

**= RÉANIMATION NÉONATALE NÉCESSAIRE**

**MESURES GÉNÉRALES:**

**HYGIÈNE:**
• Précautions d'asepsie maximales
• Gants stériles
• Masque, charlotte

**THERMORÉGULATION:**
• **Priorité absolue**
• Sécher immédiatement
• Source de chaleur radiante si disponible
• Peau-à-peau si état le permet
• Bonnet, couverture
• Sac plastique pour grands prématurés (< 32 SA)

**LIBÉRATION DES VOIES AÉRIENNES:**
• Aspiration douce bouche et nez
• Position tête en position neutre (légère extension)
• Si méconium épais: aspiration trachéale sous laryngoscopie

**SURVEILLANCE:**
• Saturation pré-ductale (main droite)
• FC
• Température
• Glycémie

**VENTILATION:**

**INDICATIONS:**
• Apnée ou gasping
• FC < 100 bpm malgré stimulation
• SpO2 en dessous des objectifs

**VPP (Ventilation en Pression Positive):**
• Masque adapté (couvre nez et bouche, pas les yeux)
• FiO2 initiale: 21% (air ambiant) si terme, 30% si prématuré
• Fréquence: 40-60/min
• PIP: 20-25 cmH2O initialement
• PEP: 5 cmH2O
• Durée: au moins 30 secondes avant réévaluation

**CRITÈRES D'EFFICACITÉ:**
• FC augmente
• SpO2 s'améliore
• Mouvements thoraciques symétriques
• Cris

**INTUBATION:**

**INDICATIONS:**
• VPP inefficace après optimisation
• Nécessité de MCE
• Suspicion hernie diaphragmatique
• Prématuré < 28 SA nécessitant ventilation

**MATÉRIEL:**
• Sonde selon poids/âge gestationnel
• Laryngoscope lame droite

**CIRCULATION:**

**MCE:**

**INDICATIONS:**
• FC < 60 bpm après 30 sec de VPP efficace

**TECHNIQUE:**
• 2 pouces sur tiers inférieur du sternum
• Profondeur: 1/3 du diamètre antéro-postérieur du thorax
• Fréquence: 90/min
• Rapport: 3 compressions / 1 insufflation

**MÉDICAMENTS:**

**ADRÉNALINE:**
• **Indication:** FC < 60 bpm après 30 sec de VPP + MCE efficaces
• **Dose:** 10-30 µg/kg IV
• **Voie:** intra-veineuse ombilicale ou IO
• **Préparation:** 0,1 ml/kg de solution à 1:10000 (0,1 mg/ml)
• Renouveler toutes les 3-5 min si besoin

**EXPANSION VOLÉMIQUE:**
• **Indication:** hypovolémie, pâleur, choc
• **NaCl 0,9%:** 10 ml/kg IV lent sur 5-10 min

**GLUCOSE:**
• Si hypoglycémie: G10% 2 ml/kg IV lent

**BICARBONATE:**
• Uniquement si acidose documentée et ventilation efficace
• 1-2 mEq/kg IV lent

**RÉÉVALUATION:**
• Toutes les 30 secondes
• Objectifs:
  - FC > 100 bpm
  - SpO2 selon âge
  - Respiration spontanée
  - Bon tonus

**ARRÊT DE LA RÉANIMATION:**
• Discuter après 10 minutes si absence totale de réponse
• Décision en concertation avec l'équipe et régulation

**TRANSPORT:**
• Médicalisé urgent vers réanimation néonatale
• Maintien ventilation et température
• Glucose IV
• Surveillance continue`
    },
    {
      id: 'reanimation-nouveau-ne',
      name: 'Algorithme Réanimation Nouveau-Né',
      icon: Activity,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**ALGORITHME DE RÉANIMATION DU NOUVEAU-NÉ**

**ÉVALUATION INITIALE (dans les 30 premières secondes):**

**3 QUESTIONS:**
1. Nouveau-né à terme ?
2. Pleure ou respire ?
3. Bon tonus musculaire ?

**SI OUI aux 3 questions = BAVEU:**
• Soins de routine
• Peau-à-peau avec la mère
• Surveillance

**SI NON à au moins 1 question = MAVEU:**
• Débuter réanimation

**ÉTAPES DE RÉANIMATION:**

**ÉTAPE 1 - STABILISATION (30 premières secondes):**
• Sécher, réchauffer
• Positionner (tête en position neutre)
• Aspiration si nécessaire (bouche puis nez)
• Stimulation tactile

**RÉÉVALUATION:** Respiration et FC

**ÉTAPE 2 - VENTILATION (si FC < 100 ou apnée/gasping):**
• VPP au masque
• FiO2: 21% si terme, 30% si prématuré
• Fréquence: 40-60/min
• PIP: 20-25 cmH2O, PEP: 5 cmH2O
• **Durée:** 30 secondes minimum

**RÉÉVALUATION:** FC après 30 sec de VPP

**OPTIMISATION SI INEFFICACE:**
• MR SOPA:
  - M: Masque (ajuster)
  - R: Repositionner la tête
  - S: Suction (aspiration)
  - O: Ouvrir la bouche
  - P: Pression (augmenter PIP)
  - A: Airway alternative (VL, intubation)

**ÉTAPE 3 - MASSAGE CARDIAQUE (si FC < 60 après VPP efficace):**
• Technique 2 pouces
• Fréquence: 90 compressions/min
• Ratio: 3:1 (3 compressions / 1 insufflation)
• **Durée:** 30 secondes minimum

**AUGMENTER FiO2 à 100%**

**RÉÉVALUATION:** FC après 30 sec de MCE + VPP

**ÉTAPE 4 - ADRÉNALINE (si FC < 60 après 30 sec MCE + VPP):**
• **Dose:** 10-30 µg/kg IV
• **Voie:** cathéter veine ombilicale ou IO
• **Préparation:** 0,1-0,3 ml/kg de solution 1:10000

**Continuer MCE + VPP**

**RÉÉVALUATION:** toutes les 30-60 secondes

**RENOUVELER ADRÉNALINE:** toutes les 3-5 min si besoin

**ÉTAPE 5 - EXPANSION VOLÉMIQUE (si hypovolémie):**
• NaCl 0,9%: 10 ml/kg IV lent sur 5-10 min

**OBJECTIFS:**
• **FC:** > 100 bpm
• **SpO2 (pré-ductale):**
  - 2 min: ≥ 60%
  - 5 min: ≥ 80%
  - 10 min: ≥ 90%
• **Respiration:** spontanée et efficace
• **Tonus:** bon

**SITUATIONS PARTICULIÈRES:**

**LIQUIDE MÉCONIAL:**
• Si nouveau-né vigoureux: soins de routine
• Si déprimé: aspiration trachéale sous laryngoscopie

**HERNIE DIAPHRAGMATIQUE:**
• Ne pas ventiler au masque
• Intubation immédiate
• SNG en aspiration
• Décubitus latéral côté sain

**PRÉMATURÉ < 32 SA:**
• Sac plastique sans sécher
• FiO2 30% initialement
• VPP d'emblée si <28 SA
• CPAP nasale précoce si respiration spontanée

**ARRÊT DE LA RÉANIMATION:**
• Pas de FC après 10 minutes de réanimation bien conduite
• Décision collégiale avec régulation

**COMMUNICATION AVEC LES PARENTS:**
• Information régulière
• Soutien psychologique
• Explication gestes techniques

**TRANSPORT:**
• Médicalisé vers réanimation néonatale
• Maintien des fonctions vitales
• Surveillance continue
• Thermorégulation stricte`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-xl border border-pink-400/30">
            <Baby className="w-8 h-8 text-pink-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Référentiels Pédiatrie-Néonatologie</h1>
            <p className="text-[hsl(var(--text-dim))]">Protocoles urgences pédiatriques et néonatales - SMUR 2025</p>
          </div>
        </div>
        
        <Alert className="bg-pink-500/10 border-pink-400/30">
          <Baby className="w-4 h-4 text-pink-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>Spécificités pédiatriques :</strong> Posologies adaptées au poids, particularités anatomiques et physiologiques
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Stethoscope className="w-5 h-5 text-pink-400" />
            Protocoles Pédiatriques et Néonataux
          </CardTitle>
          <CardDescription>
            Sélectionnez un protocole pour afficher les recommandations détaillées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {protocols.map((protocol) => {
                const Icon = protocol.icon;
                const isLoading = aiLoading[protocol.id];
                const aiResult = aiResults[protocol.id];

                return (
                  <AccordionItem
                    key={protocol.id}
                    value={protocol.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                  >
                    <AccordionTrigger
                      className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                      data-testid={`accordion-trigger-${protocol.id}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className={`p-2 ${protocol.bg} rounded-lg border ${protocol.border}`}>
                          <Icon className={`w-5 h-5 ${protocol.color}`} />
                        </div>
                        <span className="font-semibold text-[hsl(var(--text))] text-left">
                          {protocol.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 space-y-4">
                      <div className="space-y-4">
                        {/* Contenu médical */}
                        <div className="prose prose-invert max-w-none">
                          <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                            {protocol.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return (
                                  <h3 key={idx} className="text-lg font-semibold text-cyan-400 mt-4 mb-2">
                                    {line.replace(/\*\*/g, '')}
                                  </h3>
                                );
                              }
                              if (line.startsWith('• ') || line.startsWith('- ')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 my-1">
                                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              if (line.startsWith('⚠️')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              return <p key={idx} className="my-1">{line}</p>;
                            })}
                          </div>
                        </div>

                        {/* Bouton Analyse AI */}
                        <Button
                          onClick={() => handleAiAnalysis(protocol.id, protocol.name, protocol.content)}
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                          data-testid={`ai-button-${protocol.id}`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse AI en cours...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analyse AI de ce protocole
                            </>
                          )}
                        </Button>

                        {/* Résultat AI */}
                        {aiResult && (
                          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Analyse AI
                            </h3>
                            <div className="prose prose-invert max-w-none">
                              <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${protocol.id}`}>
                                {aiResult}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
